import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import React, { useId, useRef, useState } from 'react';

import { OVERLAY_Z_INDEX, POPOVER_Z_INDEX } from './inlineEditLayers';
import { SelectMenu } from '../../../../../components/atoms/SelectMenu';
import { DropdownItem } from '../../../../../components/atoms/SelectMenu/SelectMenu.props';

export interface EditableSelectProps {
  /** Visible caption above the value; may be empty when a parent (e.g. FieldItem) provides it. */
  label: string;
  /** Accessible name for the select control; falls back to `label`. Pass this when `label` is empty. */
  ariaLabel?: string;
  /** Current selected value (the option's `value`, e.g. an action-type code). */
  value?: string;
  /** Read-only display text (the option's label, e.g. the action-type name). */
  displayValue: string;
  options: DropdownItem[];
  /** Optional caption shown under the select (e.g. a side-effect warning). */
  helperText?: string;
  /** When omitted the field is read-only (no edit affordance). */
  onSave?: (value: string) => void;
}

/**
 * Editable dropdown field — click the pencil to open a popover with a
 * SelectMenu and confirm with Save. Mirrors EditableDate so a value change
 * with side effects (e.g. action type → allocation/SLA) stays deliberate.
 */
export const EditableSelect: React.FC<EditableSelectProps> = ({
  label,
  ariaLabel,
  value,
  displayValue,
  options,
  helperText,
  onSave,
}) => {
  const anchorRef = useRef<HTMLElement>(null);
  const selectId = useId();
  const accessibleName = ariaLabel || label;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const handleOpen = () => {
    setDraft(value ?? '');
    setError('');
    setOpen(true);
  };

  const handleSave = () => {
    if (!onSave) return;
    if (!draft) {
      setError('Please select a value');
      return;
    }
    setOpen(false);
    onSave(draft);
  };

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            fontSize: '9px',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            lineHeight: 1.2,
            mb: 0.25,
          }}
        >
          {label}
        </Typography>
      )}
      <Box ref={anchorRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box component="span" sx={{ fontSize: '13px', lineHeight: 1.3 }}>
          {displayValue}
        </Box>
        {onSave && (
          <IconButton
            size="small"
            onClick={handleOpen}
            aria-label={`Edit ${accessibleName || 'value'}`}
            sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'v4.accent' } }}
          >
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: POPOVER_Z_INDEX }}
      >
        {/*
          Close on mousedown, not click. The dropdown's backdrop opens under the
          pointer between mousedown and mouseup, so the browser reports the
          resulting `click` against <body> — indistinguishable from a genuine
          click away, which closed the popover the instant the menu appeared.
          mousedown is unambiguous: it lands on the select, and clicks inside the
          portalled menu still reach this listener through the React tree.
        */}
        <ClickAwayListener mouseEvent="onMouseDown" onClickAway={() => setOpen(false)}>
          <Paper
            elevation={8}
            sx={{
              p: 2,
              mt: 0.5,
              border: '1px solid',
              borderColor: 'v4.border',
              borderRadius: 2,
              width: 280,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <SelectMenu
                id={selectId}
                label={accessibleName}
                items={options}
                selectedValue={draft}
                onChange={(v: string) => {
                  setDraft(v);
                  setError('');
                }}
                hideInlineLabel
                menuZIndex={OVERLAY_Z_INDEX}
              />
              {helperText && (
                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                  {helperText}
                </Typography>
              )}
              {error && (
                <Typography sx={{ fontSize: '11px', color: 'error.main' }}>{error}</Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setOpen(false)}
                  sx={{
                    fontSize: '11px',
                    px: 1.5,
                    py: 0.5,
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    color: 'text.secondary',
                    fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleSave}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    border: 'none',
                    borderRadius: '4px',
                    bgcolor: 'v4.accent',
                    color: 'common.white',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    '&:hover': { opacity: 0.9 },
                  }}
                >
                  Save
                </Box>
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
